import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";
import { alertMessage } from "../actions/common";
import { errorHandler } from "app/shared/Service/errorHandler";
import { createQuery } from "app/utils/constants/common";
import {
  BlockedMealsList,
  CreateBlockedListPayload,
  PaginationInterfaceLF,
  UpdateBlockedListPayload,
} from "app/types";
import { RootState } from "../reducers/hooks";
import {
  assignFoodTagsAPI,
  createBlockedListAPI,
  createFoodTagsAPI,
  getBlockedListAPI,
  getFoodTagsGetByIdApi,
  getFoodTagsListApi,
  getRecipeFoodTagsAPI,
  removeFoodTagsAPI,
  sortFoodTagsAPI,
  updateBlockedListAPI,
  updateMacroNutritionalFactsAPI,
  updateFoodTagsAPI,
  syncOdooRecipesAPI,
  putLocalizedDishApi,
  getLocalizedDishAPI,
  postLocalizedDishApi,
  deleteLocalizedDishApi,
  getServingInstructionIconsApi,
  createServingInstructionIconApi,
  updateServingInstructionIconApi,
  getDishAuditLogsAPI,
} from "app/services/meals";
import {
  GetMacroRecipeNutritionalFactsAPIParams,
  MacroNutrition,
  SingleFoodTag,
  RecipeFoodTag,
  UpdateMacroNutritionalFactsActionParams,
  FoodTag,
  LocalizedDishPayload,
  localizedDish,
  ServingInstructionIcon,
  DishAuditLog,
} from "app/types/meals";
import { getMacroRecipeNutritionalFactsAPI } from "app/services/meals";
import { SET_RECIPE_DETAIL } from "app/utils/constants/meals";
interface BlockMealSelectionFilters {
  status?: string;
  search?: string;
}
interface InitialState {
  blockedMealsList: BlockedMealsList[];
  blockedMealSelectionListPagination: PaginationInterfaceLF;
  blockedMealSelectionListRowsPerPAge: number;
  isBlockedMealsListLoading: boolean;
  filters: BlockMealSelectionFilters | null;
  macroNutritionFacts: MacroNutrition[];
  isMacroNutritionLoading: boolean;
  isFoodTagsLoading: boolean;
  foodTagsList: FoodTag[];
  singleFoodTag: SingleFoodTag | null;
  recipeFoodTags: RecipeFoodTag[];
  syncOdooRecepesLoading: boolean;
  LocalizedDish: localizedDish[];
  localizedMealsPagination: PaginationInterfaceLF | null;
  localizedMealsListRowPerPage: number;
  servingInstructionIcons: ServingInstructionIcon[];
  dishAuditLogs: DishAuditLog[];
  isDishAuditLogsLoading: boolean;
  dishAuditLogsDishId: number | null;
}

const initialState: InitialState = {
  blockedMealsList: [],
  blockedMealSelectionListRowsPerPAge: 20,
  filters: null,
  blockedMealSelectionListPagination: null,
  isBlockedMealsListLoading: false,
  macroNutritionFacts: [],
  isMacroNutritionLoading: false,
  isFoodTagsLoading: false,
  foodTagsList: [],
  singleFoodTag: null,
  recipeFoodTags: [],
  syncOdooRecepesLoading: false,
  LocalizedDish: [],
  localizedMealsPagination: null,
  localizedMealsListRowPerPage: 10,
  servingInstructionIcons: [],
  dishAuditLogs: [],
  isDishAuditLogsLoading: false,
  dishAuditLogsDishId: null,
};

export const mealsSlice = createSlice({
  name: "mealsSlice",
  initialState,
  reducers: {
    setBlockedMealsList: (
      state,
      action: PayloadAction<{
        list: BlockedMealsList[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.blockedMealsList = action.payload.list;
      state.blockedMealSelectionListPagination = action.payload.pagination;
    },

    setBlockedMealSelectionListLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isBlockedMealsListLoading = action.payload;
    },
    setBlockedMealsListRowPerPage: (state, action: PayloadAction<number>) => {
      state.blockedMealSelectionListRowsPerPAge = action.payload;
    },
    setBlockMealSelectionFilters: (
      state,
      action: PayloadAction<BlockMealSelectionFilters | null>,
    ) => {
      state.filters = action.payload;
    },
    setMacroNutritionFacts: (
      state,
      action: PayloadAction<MacroNutrition[]>,
    ) => {
      state.macroNutritionFacts = action.payload;
    },

    setMacroNutritionLoading: (state, action: PayloadAction<boolean>) => {
      state.isMacroNutritionLoading = action.payload;
    },
    setFoodTagsList: (state, action: PayloadAction<FoodTag[]>) => {
      state.foodTagsList = action.payload;
    },
    setIsFoodTagsLoading: (state, action: PayloadAction<boolean>) => {
      state.isFoodTagsLoading = action.payload;
    },
    setFoodTagsGetById: (state, action: PayloadAction<SingleFoodTag>) => {
      state.singleFoodTag = action.payload;
    },
    setRecipeFoodTags: (state, action: PayloadAction<RecipeFoodTag[]>) => {
      state.recipeFoodTags = action.payload;
    },
    setSyncOdooRecepesLoading: (state, action: PayloadAction<boolean>) => {
      state.syncOdooRecepesLoading = action.payload;
    },
    setLocalizedMealsList: (
      state,
      action: PayloadAction<{
        meals: localizedDish[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.LocalizedDish = action.payload.meals;
      state.localizedMealsPagination = action.payload.pagination;
    },
    setLocalizedMealsListRowPerPage: (state, action: PayloadAction<number>) => {
      state.localizedMealsListRowPerPage = action.payload;
    },
    clearLocalizedMealsList: (state) => {
      state.LocalizedDish = [];
      state.localizedMealsPagination = null;
    },
    setServingInstructionIcons: (state, action: PayloadAction<ServingInstructionIcon[]>) => {
      state.servingInstructionIcons = action.payload;
    },
    setDishAuditLogs: (
      state,
      action: PayloadAction<{ logs: DishAuditLog[]; dishId: number }>,
    ) => {
      state.dishAuditLogs = action.payload.logs;
      state.dishAuditLogsDishId = action.payload.dishId;
    },
    setIsDishAuditLogsLoading: (state, action: PayloadAction<boolean>) => {
      state.isDishAuditLogsLoading = action.payload;
    },
    clearDishAuditLogs: (state) => {
      state.dishAuditLogs = [];
      state.dishAuditLogsDishId = null;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setBlockedMealsList,
  setBlockedMealsListRowPerPage,
  setBlockedMealSelectionListLoading,
  setBlockMealSelectionFilters,
  setMacroNutritionLoading,
  setMacroNutritionFacts,
  setFoodTagsList,
  setIsFoodTagsLoading,
  setFoodTagsGetById,
  setRecipeFoodTags,
  setSyncOdooRecepesLoading,
  setLocalizedMealsList,
  setLocalizedMealsListRowPerPage,
  clearLocalizedMealsList,
  setServingInstructionIcons,
  setDishAuditLogs,
  setIsDishAuditLogsLoading,
  clearDishAuditLogs,
} = mealsSlice.actions;

export default mealsSlice.reducer;

type GetBlockedListActionType = {
  continutationToken?: string;
  storeId: number;
};
export const getBlockedListAction = (
  { continutationToken, storeId }: GetBlockedListActionType = {
    continutationToken: null,
    storeId: null,
  },
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { filters, blockedMealSelectionListRowsPerPAge } =
        getState().mealsSlice;

      if (!storeId) {
        dispatch(alertMessage("No Store found", "error"));
      }
      let query = "";
      query =
        "?" +
        createQuery({
          filter: {
            ...(filters || {}),
            limit: blockedMealSelectionListRowsPerPAge,
            continutationToken,
          },
        });
      dispatch(setBlockedMealSelectionListLoading(true));
      const { data } = await getBlockedListAPI(storeId, query);
      dispatch(setBlockedMealSelectionListLoading(false));
      const pagination = data.pagination;
      dispatch(
        setBlockedMealsList({
          list: data.data,
          pagination,
        }),
      );
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setBlockedMealSelectionListLoading(false));
      dispatch(alertMessage(message, "error"));
    }
  };
};
export const createBlockListAction =
  (storeId: number, payload: CreateBlockedListPayload) =>
    async (dispatch: AppDispatch) => {
      try {
        const { data } = await createBlockedListAPI(storeId, payload);
        if (data.data) {
          dispatch(alertMessage("Created successfully", "success"));
          // We refresh the  data
          dispatch(getBlockedListAction({ continutationToken: null, storeId }));
        }
      } catch (error) {
        dispatch(alertMessage("Something went wrong", "error"));
      }
    };

export const updateBlockListAction =
  (storeId: number, payload: UpdateBlockedListPayload) =>
    async (dispatch: AppDispatch) => {
      try {
        await updateBlockedListAPI(storeId, payload);
        // We refresh the  data
        dispatch(getBlockedListAction({ continutationToken: null, storeId }));
        dispatch(alertMessage("Updated successfully", "success"));
      } catch (error) {
        dispatch(alertMessage("Error updating the status", "error"));
      }
    };

export const getMacroNutritionFactsAction =
  ({ dishId, recipeId }: GetMacroRecipeNutritionalFactsAPIParams) =>
    async (dispatch: AppDispatch) => {
      try {
        dispatch(setMacroNutritionLoading(true));

        const response = await getMacroRecipeNutritionalFactsAPI({
          dishId,
          recipeId,
        });
        const rawData = response.data.data;

        // Inline data transformation
        const groupedMap = new Map<string, MacroNutrition[]>();
        rawData.forEach((item) => {
          const key = `${item.macroProtein}-${item.macroCarbs}`;
          if (!groupedMap.has(key)) {
            groupedMap.set(key, []);
          }
          groupedMap.get(key)!.push(item);
        });

        const flattened = [];
        groupedMap.forEach((items, key) => {
          const [protein, carbs] = key.split("-");
          flattened.push({
            isGroupHeader: true,
            groupLabel: `MacroProtein: ${protein} / MacroCarbs: ${carbs}`,
          });
          flattened.push(...items);
        });

        dispatch(setMacroNutritionFacts(flattened));
      } catch (error) {
        const message = errorHandler(error);
        dispatch(alertMessage(message, "error"));
      } finally {
        dispatch(setMacroNutritionLoading(false));
      }
    };

export const updateMacroNutritionalFactsAction = ({
  apiParams,
}: UpdateMacroNutritionalFactsActionParams) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { recipeDetail } = getState().meals;

      const { data } = await updateMacroNutritionalFactsAPI(apiParams);
      const updatedRecipeDetail = data.data;

      dispatch({
        type: SET_RECIPE_DETAIL,
        payload: {
          ...recipeDetail,
          calories: updatedRecipeDetail[0].calories,
          protein: updatedRecipeDetail[0].protein,
          carbs: updatedRecipeDetail[0].carbs,
          fat: updatedRecipeDetail[0].fat,
          macroProtein: updatedRecipeDetail[0].macroProtein,
          macroCarbs: updatedRecipeDetail[0].macroCarbs,
        },
      });
      dispatch(
        getMacroNutritionFactsAction({
          dishId: apiParams.dishId,
          recipeId: apiParams.recipeId,
        }),
      );
      dispatch(alertMessage("Recipe nutritional facts updated", "success"));
    } catch (err) {
      const message = errorHandler(err);
      dispatch(alertMessage(message, "error"));
    }
  };
};
export const getFoodTagsListAction = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(setIsFoodTagsLoading(true));
    const { data } = await getFoodTagsListApi();
    dispatch(setIsFoodTagsLoading(false));
    if (data.data) {
      dispatch(setFoodTagsList(data.data));
    }
  } catch (error) {
    dispatch(alertMessage("Something went wrong", "error"));
  }
};
export interface Localization {
  locale: string;
  name: string;
}
export interface CreateFoodTagsPayload {
  name: string;
  localizations: Localization[];
}
export const createFoodTagsAction =
  (payload: CreateFoodTagsPayload) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      try {
        const response = await createFoodTagsAPI(payload);
        const createdTag: FoodTag = response.data.data;

        const { foodTagsList } = getState().mealsSlice;
        const updatedList = [...foodTagsList, createdTag];

        dispatch(setFoodTagsList(updatedList));
        dispatch(alertMessage("Created successfully", "success"));
      } catch (error) {
        dispatch(alertMessage(error.response.data.message, "error"));
      }
    };
export const getFoodTagsGetByIdAction =
  (tagId: number) => async (dispatch: AppDispatch) => {
    try {
      const { data } = await getFoodTagsGetByIdApi(tagId);
      if (data.data) {
        dispatch(setFoodTagsGetById(data.data));
      }
    } catch (error) {
      dispatch(alertMessage("Something went wrong", "error"));
    }
  };
export const updateFoodTagsAction =
  (payload: CreateFoodTagsPayload, id: number) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      try {
        const response = await updateFoodTagsAPI(payload, id);
        const updatedTag = response.data.data;

        const { foodTagsList } = getState().mealsSlice;

        const updatedList = foodTagsList
          ? foodTagsList.map((tag) => (tag.id === id ? updatedTag : tag))
          : [];

        dispatch(setFoodTagsList(updatedList));
        dispatch(alertMessage("Updated successfully", "success"));
      } catch (error) {
        dispatch(alertMessage(error.response.data.message, "error"));
      }
    };

export const getRecipeFoodTagsAction =
  (recipeId: number, dishId: number) => async (dispatch: AppDispatch) => {
    try {
      const { data } = await getRecipeFoodTagsAPI(recipeId, dishId);
      if (data.data) {
        dispatch(setRecipeFoodTags(data.data));
      }
    } catch (error) {
      dispatch(alertMessage("Something went wrong", "error"));
    }
  };
export interface SortFoodTagPayload {
  sortedFoodTags: {
    foodTagId: number;
    sort: number;
  }[];
}

export const sortFoodTagsAPIAction =
  (recipeId: number, dishId: number, payload: SortFoodTagPayload) =>
    async (dispatch: AppDispatch) => {
      try {
        await sortFoodTagsAPI(recipeId, dishId, payload);
        dispatch(alertMessage("Updated successfully", "success"));
      } catch (error) {
        dispatch(alertMessage(error.response.data.message, "error"));
      }
    };
export interface AssignFoodTagsPayload {
  addTags: number[];
}
export const assignFoodTagsAction =
  (recipeId: number, dishId: number, payload: AssignFoodTagsPayload) =>
    async (dispatch: AppDispatch) => {
      try {
        await assignFoodTagsAPI(recipeId, dishId, payload);
        dispatch(alertMessage("Updated successfully", "success"));
      } catch (error) {
        dispatch(alertMessage(error.response.data.message, "error"));
      }
    };
export interface RemoveFoodTagsPayload {
  removeTags: number[];
}
export const removeFoodTagsAction =
  (recipeId: number, dishId: number, payload: RemoveFoodTagsPayload) =>
    async (dispatch: AppDispatch) => {
      try {
        await removeFoodTagsAPI(recipeId, dishId, payload);
        dispatch(alertMessage("Removed successfully", "success"));
      } catch (error) {
        dispatch(alertMessage(error.response.data.message, "error"));
      }
    };

export const syncOdooRecipesAction =
  (dishId: number) => async (dispatch: AppDispatch) => {
    try {
      dispatch(setSyncOdooRecepesLoading(true));
      await syncOdooRecipesAPI(dishId);
      dispatch(setSyncOdooRecepesLoading(false));
      dispatch(alertMessage("Recipes synced successfully", "success"));
    } catch (error) {
      dispatch(setSyncOdooRecepesLoading(false));
      dispatch(alertMessage("Something went wrong", "error"));
    }
  };

export const deleteLocalizedDishAction = (dishId: number, locale: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await deleteLocalizedDishApi(dishId, locale);
      dispatch(alertMessage("Localized meal deleted successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
export const postLocalizedDishAction = (
  dishId: number,
  payload: LocalizedDishPayload,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await postLocalizedDishApi(dishId, payload);
      dispatch(alertMessage("Localized meal added successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getLocalizedDishAction = (
  dishId: number,
  continutationToken: string,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { localizedMealsListRowPerPage } = getState().mealsSlice;
      let query = "";
      query =
        "?" +
        createQuery({
          filter: {
            limit: localizedMealsListRowPerPage,
            continutationToken,
          },
        });

      const { data } = await getLocalizedDishAPI(dishId, query);

      const pagination = data.pagination;
      const meals = data.data;
      dispatch(setLocalizedMealsList({ meals, pagination }));
    } catch (error) {
      dispatch(clearLocalizedMealsList());
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
export const putLocalizedDishAction = (
  dishId: number,
  payload: LocalizedDishPayload,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await putLocalizedDishApi(dishId, payload);
      dispatch(alertMessage("Localized meal updated successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getServingInstructionIconsAction = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await getServingInstructionIconsApi();
      dispatch(setServingInstructionIcons(data.data));
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const createServingInstructionIconAction = (payload: { name: string; url?: string; icon?: string }) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await createServingInstructionIconApi(payload);
      dispatch(alertMessage("Serving instruction icon created successfully", "success"));
      setTimeout(() => {
        dispatch(getServingInstructionIconsAction());
      }, 500);
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const updateServingInstructionIconAction = (id: number, payload: { name: string; url?: string; icon?: string }) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await updateServingInstructionIconApi(id, payload);
      dispatch(alertMessage("Serving instruction icon updated successfully", "success"));
      setTimeout(() => {
        dispatch(getServingInstructionIconsAction());
      }, 500);
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getDishAuditLogsAction = (dishId: number) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const { dishAuditLogsDishId } = getState().mealsSlice;
    if (dishAuditLogsDishId !== dishId) {
      dispatch(clearDishAuditLogs());
    }
    try {
      dispatch(setIsDishAuditLogsLoading(true));
      const { data } = await getDishAuditLogsAPI(dishId);
      dispatch(
        setDishAuditLogs({
          logs: data.data ?? [],
          dishId,
        }),
      );
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    } finally {
      dispatch(setIsDishAuditLogsLoading(false));
    }
  };
};
